// Game-restart banner — surfaces "The game was restarted by {name}" after
// a restart-driven reload. Reads the sessionStorage notice flag that
// game-map's _reloadForRestart writes before triggering window.location
// .reload(), then exposes a single Close (X) action that drops the flag
// and hides the banner.
//
// One-shot: there's no "don't show again" persistence — the banner is
// scoped to a single restart event and goes away the moment the player
// dismisses it (or the tab closes, since the flag lives in
// sessionStorage).

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

const NOTICE_KEY_PREFIX = 'game-restart-notice:';

class GameRestartBanner extends BaseComponent {
  static properties = {
    gameId: { type: String, attribute: 'game-id' },
    _hidden: { type: Boolean, state: true },
    _restarterName: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
    .alert {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      margin: 0;
      border-radius: 0;
    }
    .alert .body {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex: 1;
    }
    .alert .body i {
      flex-shrink: 0;
    }
    .btn-close {
      background: transparent;
      border: none;
      color: inherit;
      font-size: 1.125rem;
      line-height: 1;
      cursor: pointer;
      padding: 0.25rem;
    }
    .btn-close:hover {
      opacity: 0.75;
    }
  `;

  constructor() {
    super();
    this.gameId = '';
    this._hidden = false;
    this._restarterName = '';
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._readNotice();
  }

  /** @param {Map<string, unknown>} changed */
  updated(changed) {
    if (changed.has('gameId')) {
      this._readNotice();
    }
  }

  _readNotice() {
    if (!this.gameId) {
      this._restarterName = '';
      return;
    }
    try {
      const raw = sessionStorage.getItem(`${NOTICE_KEY_PREFIX}${this.gameId}`);
      if (!raw) {
        this._restarterName = '';
        return;
      }
      const parsed = JSON.parse(raw);
      this._restarterName = typeof parsed?.restarterName === 'string' ? parsed.restarterName : '';
      this._hidden = false;
    } catch {
      this._restarterName = '';
    }
  }

  _dismiss() {
    if (this.gameId) {
      try {
        sessionStorage.removeItem(`${NOTICE_KEY_PREFIX}${this.gameId}`);
      } catch {
        /* sessionStorage unavailable — banner state still hides */
      }
    }
    this._hidden = true;
  }

  render() {
    if (this._hidden || !this._restarterName) return '';
    return html`
      <div class="alert alert-info" role="status">
        <div class="body">
          <i class="bi bi-info-circle-fill" aria-hidden="true"></i>
          <span>The game was restarted by ${this._restarterName}.</span>
        </div>
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          @click="${this._dismiss}"
        >
          <i class="bi bi-x-lg" aria-hidden="true"></i>
        </button>
      </div>
    `;
  }
}

if (!customElements.get('game-restart-banner')) {
  customElements.define('game-restart-banner', GameRestartBanner);
}
